import React from "react";
import { NavLink } from "react-router-dom";

/**
 * The routed tab role of the design system (design-system-spec §4.2): a
 * segmented bar of real NavLink anchors — deep-linkable, `end` for sibling
 * routes. Inline by default; `sticky` renders the full-bleed bar that pins
 * under the portal header (replaces the old per-portal ModuleTabsHeader),
 * with an optional right-aligned `action`.
 *
 * The sticky bar breaks out of the shell's p-8 content gutter with -mx-8/-mt-8
 * — that pairing is a contract with the shell padding (spec §1.3); change both
 * together.
 */
export interface SectionTab {
  label: string;
  href: string;
  end?: boolean;
}

const CHIP =
  "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
const CHIP_ACTIVE = "bg-background text-foreground shadow-sm";
const CHIP_IDLE = "text-muted-foreground hover:text-foreground";

/** Full-bleed sticky bar chrome shared by routed and local-state tab headers. */
export function StickyTabsBar({ children, className = "", barRef }: {
  children: React.ReactNode;
  className?: string;
  /** Ref to the sticky bar element, so callers can anchor overlays to it. */
  barRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={barRef}
      className={`sticky top-0 z-20 -mx-8 -mt-8 mb-4 flex h-14 items-center gap-4 border-b bg-background px-4 ${className}`}
    >
      {children}
    </div>
  );
}

export function SectionTabs({ items, sticky = false, action, className = "" }: {
  items: SectionTab[];
  /** Pin the bar full-bleed under the portal header. */
  sticky?: boolean;
  /** Right-aligned toolbar content; only rendered in sticky mode. */
  action?: React.ReactNode;
  className?: string;
}) {
  const nav = (
    <nav className={`inline-flex items-center gap-1 rounded-lg bg-muted p-1 ${sticky ? "shrink-0" : className}`}>
      {items.map(({ label, href, end }) => (
        <NavLink
          key={href}
          to={href}
          end={end}
          className={({ isActive }) => `${CHIP} ${isActive ? CHIP_ACTIVE : CHIP_IDLE}`}
        >
          {label}
        </NavLink>
      ))}
    </nav>
  );

  if (!sticky) return nav;

  return (
    <StickyTabsBar className={`justify-between ${className}`}>
      {nav}
      {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
    </StickyTabsBar>
  );
}

export interface LocalTab<Id extends string = string> {
  id: Id;
  label: string;
}

/**
 * The same sticky tab bar for tabs that are local page state rather than
 * routes (e.g. an editor's General / Content split), with an optional centre
 * slot (e.g. a viewport control) and an actions slot on the right (Save /
 * Preview / status badges).
 */
export function LocalTabsHeader<Id extends string>({
  tabs,
  active,
  onSelect,
  center,
  actions,
  barRef,
}: {
  tabs: LocalTab<Id>[];
  active: Id;
  onSelect: (id: Id) => void;
  center?: React.ReactNode;
  actions?: React.ReactNode;
  barRef?: React.Ref<HTMLDivElement>;
}) {
  return (
    <StickyTabsBar barRef={barRef}>
      <nav className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-muted p-1">
        {tabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => onSelect(id)}
            className={`${CHIP} ${active === id ? CHIP_ACTIVE : CHIP_IDLE}`}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="flex flex-1 items-center justify-center">{center}</div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </StickyTabsBar>
  );
}
